/**
 * Google Maps JS API loader.
 * Injects the script tag once and caches the loading promise so repeated
 * calls (e.g. re-opening the location modal) don't reload the script.
 *
 * Requires REACT_APP_GOOGLE_MAPS_API_KEY in .env, with Maps JavaScript API,
 * Places API, and Geocoding API enabled, restricted by HTTP referrer to
 * this panel's domain(s). Do NOT reuse the mobile app's Maps key here — it's
 * restricted to the Android/iOS app and won't work (or shouldn't be relied
 * on) for a website.
 */

const SCRIPT_ID = "google-maps-js-sdk";

let loadPromise = null;

export const isGoogleMapsConfigured = () =>
  Boolean(process.env.REACT_APP_GOOGLE_MAPS_API_KEY);

/**
 * @returns {Promise<typeof window.google>} resolves once window.google.maps
 * (with the `places` library) is ready; rejects if no key is configured or
 * the script fails to load.
 */
export const loadGoogleMaps = () => {
  if (loadPromise) return loadPromise;

  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  if (!apiKey) {
    return Promise.reject(new Error("REACT_APP_GOOGLE_MAPS_API_KEY is not configured."));
  }

  if (window.google?.maps?.places) {
    loadPromise = Promise.resolve(window.google);
    return loadPromise;
  }

  loadPromise = new Promise((resolve, reject) => {
    const existing = document.getElementById(SCRIPT_ID);
    if (existing) {
      existing.addEventListener("load", () => resolve(window.google));
      existing.addEventListener("error", () => reject(new Error("Failed to load Google Maps script.")));
      return;
    }

    const script = document.createElement("script");
    script.id = SCRIPT_ID;
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve(window.google);
    script.onerror = () => {
      loadPromise = null;
      reject(new Error("Failed to load Google Maps script."));
    };
    document.head.appendChild(script);
  });

  return loadPromise;
};
